import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Edit2, PenSquare, RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";
import type { DraftPending } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { useRealtime } from "@/lib/realtime";
import { ComposeDraftDialog } from "@/components/ComposeDraftDialog";

const KIND_LABELS: Record<string, string> = {
  member_reply: "Member reply",
  paid_send: "Paid send",
  recruiter_outreach: "Recruiter outreach",
};

export function Drafts() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["drafts"],
    queryFn: () => api.drafts({ limit: 100 }),
    refetchInterval: 15_000,
  });
  useRealtime("drafts_pending", () => qc.invalidateQueries({ queryKey: ["drafts"] }));

  const drafts = q.data ?? [];
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [creatorFilter, setCreatorFilter] = React.useState<string>("");
  const [kindFilter, setKindFilter] = React.useState<string>("");
  const [bulkPending, setBulkPending] = React.useState(false);
  const [composeOpen, setComposeOpen] = React.useState(false);

  const creators = React.useMemo(() => {
    const s = new Set<string>();
    drafts.forEach((d) => s.add(d.creator_handle));
    return Array.from(s).sort();
  }, [drafts]);

  const filtered = React.useMemo(
    () =>
      drafts.filter(
        (d) =>
          (!creatorFilter || d.creator_handle === creatorFilter) &&
          (!kindFilter || d.kind === kindFilter),
      ),
    [drafts, creatorFilter, kindFilter],
  );

  // Trim selection if filter excluded an item.
  React.useEffect(() => {
    setSelected((prev) => {
      const visibleIds = new Set(filtered.map((d) => d.draft_id));
      const next = new Set<string>();
      prev.forEach((id) => visibleIds.has(id) && next.add(id));
      return next;
    });
  }, [filtered]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelected(new Set(filtered.map((d) => d.draft_id)));
  const clearSelection = () => setSelected(new Set());

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Approve & send ${selected.size} draft${selected.size === 1 ? "" : "s"}?`)) return;
    setBulkPending(true);
    try {
      // Sequential to avoid hammering bridge sender. ~150ms each is fine.
      for (const id of selected) {
        try {
          await api.approveDraft(id);
        } catch (e) {
          console.error("approve failed", id, e);
        }
      }
      setSelected(new Set());
      await q.refetch();
    } finally {
      setBulkPending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Drafts</h1>
          <p className="text-sm text-muted-foreground">
            Messages awaiting your approval before they go out. Supervised mode
            and paid sends route here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setComposeOpen(true)}
          >
            <PenSquare className="mr-2 h-4 w-4" />
            Compose
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
          >
            <RefreshCw className={"mr-2 h-4 w-4 " + (q.isFetching ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
      </div>
      <ComposeDraftDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        onComposed={() => q.refetch()}
      />

      {/* Filters + bulk action bar */}
      {drafts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card/40 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Filter:</span>
          <select
            value={creatorFilter}
            onChange={(e) => setCreatorFilter(e.target.value)}
            className="h-7 rounded border bg-background px-2 text-xs"
          >
            <option value="">All creators</option>
            {creators.map((c) => (
              <option key={c} value={c}>
                @{c}
              </option>
            ))}
          </select>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="h-7 rounded border bg-background px-2 text-xs"
          >
            <option value="">All kinds</option>
            {Object.entries(KIND_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
          {(creatorFilter || kindFilter) && (
            <button
              onClick={() => {
                setCreatorFilter("");
                setKindFilter("");
              }}
              className="text-2xs text-muted-foreground hover:text-foreground"
            >
              clear
            </button>
          )}
          <span className="ml-auto text-muted-foreground">
            {filtered.length} of {drafts.length}
          </span>

          {/* Selection actions */}
          <div className="ml-2 flex items-center gap-2 border-l pl-2">
            <button
              onClick={
                selected.size === filtered.length && filtered.length > 0
                  ? clearSelection
                  : selectAllVisible
              }
              className="text-xs text-muted-foreground hover:text-foreground"
              disabled={filtered.length === 0}
            >
              {selected.size === filtered.length && filtered.length > 0
                ? "deselect all"
                : "select all"}
            </button>
            {selected.size > 0 && (
              <Button
                size="sm"
                onClick={bulkApprove}
                disabled={bulkPending}
              >
                <CheckCircle2 className="mr-1 h-3 w-3" />
                {bulkPending
                  ? `Approving ${selected.size}…`
                  : `Approve ${selected.size} selected`}
              </Button>
            )}
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
        </div>
      ) : drafts.length === 0 ? (
        <Empty
          title="Inbox zero"
          hint="Nothing waiting for approval. Either everything is autonomous or there hasn't been activity."
        />
      ) : filtered.length === 0 ? (
        <Empty title="No drafts match this filter." />
      ) : (
        <div className="space-y-4">
          {filtered.map((d) => (
            <DraftCard
              key={d.draft_id}
              draft={d}
              selected={selected.has(d.draft_id)}
              onToggleSelected={() => toggle(d.draft_id)}
              onChange={() => q.refetch()}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DraftCard({
  draft,
  onChange,
  selected,
  onToggleSelected,
}: {
  draft: DraftPending;
  onChange: () => void;
  selected?: boolean;
  onToggleSelected?: () => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [editChunks, setEditChunks] = React.useState<string[]>(draft.chunks);
  const [pending, setPending] = React.useState<"approve" | "edit" | "deny" | null>(null);
  const expiresIn = expiresInLabel(draft.expires_at);

  const onApprove = async (chunks?: string[]) => {
    setPending(chunks ? "edit" : "approve");
    try {
      if (chunks) {
        await api.editDraft(draft.draft_id, chunks);
      } else {
        await api.approveDraft(draft.draft_id);
      }
      onChange();
    } catch (e) {
      alert(`Approval failed: ${(e as Error).message}`);
    } finally {
      setPending(null);
    }
  };
  const onDeny = async () => {
    const reason = prompt("Reason for denial (will be logged):");
    if (reason === null) return;
    setPending("deny");
    try {
      await api.denyDraft(draft.draft_id, reason || "no reason given");
      onChange();
    } catch (e) {
      alert(`Deny failed: ${(e as Error).message}`);
    } finally {
      setPending(null);
    }
  };

  return (
    <div
      className={
        "rounded-lg border bg-card p-4 space-y-3 " +
        (selected ? "ring-2 ring-brand/50" : "")
      }
    >
      {/* Header */}
      <div className="flex items-baseline justify-between text-xs text-muted-foreground">
        <div className="flex items-baseline gap-2">
          {onToggleSelected && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelected}
              onClick={(e) => e.stopPropagation()}
              className="h-3.5 w-3.5 cursor-pointer accent-brand"
              title="Select for bulk approve"
            />
          )}
          <span className="font-medium text-foreground">{draft.creator_handle}</span>
          <span>·</span>
          <span>{KIND_LABELS[draft.kind] ?? draft.kind}</span>
          <span>·</span>
          <span>to {draft.member_phone_redacted}</span>
        </div>
        <div>
          drafted {timeAgo(draft.drafted_at)} · expires {expiresIn}
        </div>
      </div>

      {/* Thread context — last 5 messages of conversation */}
      {draft.thread_preview && draft.thread_preview.length > 0 && (
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2">
          <div className="text-2xs uppercase text-muted-foreground mb-1">
            Conversation context (last 5)
          </div>
          <div className="space-y-1">
            {draft.thread_preview.map((m, i) => (
              <div
                key={i}
                className={
                  "flex " + (m.direction === "outbound" ? "justify-start" : "justify-end")
                }
              >
                <div
                  className={
                    "max-w-[80%] rounded-2xl px-2.5 py-1 text-xs " +
                    (m.direction === "outbound"
                      ? "bg-blue-100 text-blue-900"
                      : "bg-zinc-200 text-zinc-900")
                  }
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inbound */}
      {draft.inbound_message ? (
        <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
          <div className="text-2xs uppercase text-muted-foreground mb-0.5">
            Inbound triggering this draft
          </div>
          {draft.inbound_message}
        </div>
      ) : null}

      {/* Draft chunks */}
      <div>
        <div className="text-2xs uppercase text-muted-foreground mb-1">
          Draft ({draft.chunks.length} message{draft.chunks.length === 1 ? "" : "s"})
        </div>
        {editing ? (
          <div className="space-y-2">
            {editChunks.map((c, idx) => (
              <Textarea
                key={idx} value={c}
                rows={2}
                onChange={(e) => {
                  const next = [...editChunks];
                  next[idx] = e.target.value;
                  setEditChunks(next);
                }}
              />
            ))}
            <div className="flex gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => setEditChunks([...editChunks, ""])}
              >
                + add chunk
              </Button>
              {editChunks.length > 1 && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => setEditChunks(editChunks.slice(0, -1))}
                >
                  − remove last
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {draft.chunks.map((c, idx) => (
              <div key={idx} className="rounded-2xl bg-blue-100 text-blue-900 px-3 py-1.5 text-sm w-fit max-w-[80%]">
                {c}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer / actions */}
      <div className="flex items-center justify-between border-t pt-3">
        <div className="text-2xs text-muted-foreground italic max-w-md truncate">
          {draft.system_prompt_summary} · est ${(draft.estimated_cost_cents / 100).toFixed(4)}
        </div>
        <div className="flex gap-2">
          {!editing ? (
            <>
              <Button
                size="sm" variant="outline"
                onClick={() => { setEditing(true); setEditChunks(draft.chunks); }}
              >
                <Edit2 className="mr-1 h-3 w-3" /> Edit
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={onDeny}
                disabled={!!pending}
              >
                <X className="mr-1 h-3 w-3" /> Deny
              </Button>
              <Button
                size="sm"
                onClick={() => onApprove()}
                disabled={!!pending}
              >
                <CheckCircle2 className="mr-1 h-3 w-3" />
                {pending === "approve" ? "Sending…" : "Approve & send"}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm" variant="outline"
                onClick={() => { setEditing(false); setEditChunks(draft.chunks); }}
                disabled={!!pending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => onApprove(editChunks.filter((c) => c.trim()))}
                disabled={!!pending || editChunks.every((c) => !c.trim())}
              >
                {pending === "edit" ? "Sending…" : "Save & send"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function expiresInLabel(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `in ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `in ${hr}h`;
  return `in ${Math.floor(hr / 24)}d`;
}
